import { MARKETPLACE_SHORTLIST_MAX } from '@crm/contracts';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import type { ShortlistView } from '../../lib/client.ts';
import { marketplaceCandidate, marketplaceShortlistUrl } from '../../lib/marketplace.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';

type Place = ShortlistView['candidates'][number];

/**
 * "Generate Marketplace Shortlist": choose which of the shortlist's candidates go on the website's marketplace page
 * a hiring manager sends a client (`lib/marketplace.ts`), then open it in a new tab. The dialog
 * lists the candidates still on the shortlist (the caller leaves archived ones out), every one
 * with a marketplace code ticked to start with; those without one cannot be chosen and say why.
 * The link is offered only with at least one ticked and no more than the website takes.
 */
export function MarketplaceShortlistButton({
  shortlist,
  candidates,
}: {
  shortlist: Pick<ShortlistView, 'name'>;
  /** The places on the shortlist now (archived ones left out). */
  candidates: readonly Place[];
}) {
  const [open, setOpen] = useState(false);
  const withCode = candidates.filter((c) => c.candidate.marketplaceCode);
  const label = `Open ${shortlist.name} in the marketplace`;
  const reason =
    candidates.length === 0
      ? 'No candidates on this shortlist yet'
      : withCode.length === 0
        ? 'No candidate on this shortlist has a marketplace code'
        : null;
  if (reason)
    return (
      // A disabled button gets no pointer events, so the reason sits on its wrapper.
      <span title={reason} data-testid={`marketplace-${shortlist.name}`}>
        <Button size="sm" variant="outline" disabled aria-label={`${label}: ${reason}`}>
          <ExternalLink /> Generate Marketplace Shortlist
        </Button>
      </span>
    );
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        aria-label={label}
        data-testid={`marketplace-${shortlist.name}`}
      >
        <ExternalLink /> Generate Marketplace Shortlist
      </Button>
      {open && (
        <MarketplacePicker
          name={shortlist.name}
          candidates={candidates}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function MarketplacePicker({
  name,
  candidates,
  onClose,
}: {
  name: string;
  candidates: readonly Place[];
  onClose: () => void;
}) {
  const choosable = candidates.filter((c) => c.candidate.marketplaceCode);
  // Every candidate with a code, to start with (mounted per opening, so it starts fresh).
  const [ticked, setTicked] = useState<ReadonlySet<string>>(
    () => new Set(choosable.map((c) => c.candidateId)),
  );
  // In shortlist order, each code once (two records of one person can share a code).
  const codes = [
    ...new Set(
      candidates
        .filter((c) => ticked.has(c.candidateId))
        .map((c) => c.candidate.marketplaceCode!)
        .filter(Boolean),
    ),
  ];
  const over = codes.length > MARKETPLACE_SHORTLIST_MAX;
  const blocked =
    codes.length === 0
      ? 'Tick at least one candidate'
      : over
        ? `The marketplace shows at most ${MARKETPLACE_SHORTLIST_MAX} candidates: untick ${codes.length - MARKETPLACE_SHORTLIST_MAX}`
        : null;
  const allTicked = choosable.length > 0 && choosable.every((c) => ticked.has(c.candidateId));
  const someTicked = choosable.some((c) => ticked.has(c.candidateId));
  const toggle = (id: string, on: boolean) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="marketplace-picker">
        <DialogHeader>
          <DialogTitle>{name} on the marketplace</DialogTitle>
          <DialogDescription>
            Choose the candidates the client sees on the marketplace page.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-center gap-2 font-medium">
            <input
              type="checkbox"
              className="accent-brand size-4 cursor-pointer"
              checked={allTicked}
              ref={(el) => {
                if (el) el.indeterminate = someTicked && !allTicked;
              }}
              onChange={(e) =>
                setTicked(
                  e.target.checked ? new Set(choosable.map((c) => c.candidateId)) : new Set(),
                )
              }
              aria-label="Select all candidates with a marketplace code"
            />
            Select all
          </label>
          <ul className="border-border max-h-80 overflow-y-auto rounded-lg border">
            {candidates.map((c) => {
              const code = c.candidate.marketplaceCode;
              return (
                <li key={c.candidateId} className="border-border border-b last:border-b-0">
                  <label
                    className={cn(
                      'flex items-center gap-2 px-3 py-2',
                      code ? 'cursor-pointer' : 'text-muted-foreground',
                    )}
                    title={code ? undefined : 'No marketplace code: not on the marketplace'}
                  >
                    <input
                      type="checkbox"
                      className="accent-brand size-4 cursor-pointer"
                      disabled={!code}
                      checked={!!code && ticked.has(c.candidateId)}
                      onChange={(e) => toggle(c.candidateId, e.target.checked)}
                      aria-label={c.candidate.name}
                    />
                    <span className="min-w-0 flex-1 truncate">{c.candidate.name}</span>
                    <span className="text-muted-foreground text-xs whitespace-nowrap">
                      {code ?? 'No marketplace code'}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <p
            className={cn('text-xs', over ? 'text-danger' : 'text-muted-foreground')}
            data-testid="marketplace-count"
          >
            {codes.length} of {MARKETPLACE_SHORTLIST_MAX} candidates
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {blocked ? (
            <span title={blocked}>
              <Button disabled aria-label={`Open in Marketplace: ${blocked}`}>
                <ExternalLink /> Open in Marketplace
              </Button>
            </span>
          ) : (
            <Button asChild>
              <a
                href={marketplaceShortlistUrl(codes)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
              >
                <ExternalLink /> Open in Marketplace
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One shortlisted candidate on the marketplace, in a new tab: the website opens on them with
 * their profile open. "—" for a candidate with no marketplace code.
 */
export function MarketplaceCandidateLink({ name, code }: { name: string; code: string | null }) {
  const url = marketplaceCandidate(code);
  if (!url) return <span className="text-muted-foreground">—</span>;
  return (
    <Button size="icon" variant="ghost" className="size-8" asChild>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={`${code} on the marketplace`}
        aria-label={`Open ${name} in the marketplace`}
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="size-4" />
      </a>
    </Button>
  );
}
